import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './TournamentBracketTree.module.css';

interface Mercenary {
  name: string;
}

interface Match {
  id: number;
  team1Participants: Mercenary[];
  team2Participants: Mercenary[];
  winner_team?: number;
  round_number: number;
  battle_report?: number;
  match_type?: 'normal' | 'final' | 'third_place' | 'bye';
}

interface BracketTreeProps {
  matches: Match[];
  numTeams: number;
}

interface BracketMatch {
  match: Match | null;
  position: { x: number; y: number };
  isThirdPlace?: boolean;
}

const TournamentBracketTree: React.FC<BracketTreeProps> = ({ matches, numTeams }) => {
  const [scale, setScale] = useState(0.8); // Start slightly zoomed out to show more
  const [position, setPosition] = useState({ x: 0, y: -200 }); // Start with view shifted up to show more matches
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      // Adjust initial scale and position for mobile
      if (window.innerWidth <= 768) {
        setScale(0.5);
        setPosition({ x: 50, y: -100 });
      } else {
        setScale(0.8);
        setPosition({ x: 0, y: -200 });
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate bracket structure
  const calculateBracketStructure = (): BracketMatch[] => {
    const MATCH_SPACING = 120;
    const ROUND_SPACING = 360;
    const START_X = 60;
    const START_Y = 40;
    const rounds = Math.ceil(Math.log2(Math.max(numTeams, 2)));
    const bracketMatches: BracketMatch[] = [];

    for (let round = 1; round <= rounds; round++) {
      const roundMatches = matches.filter((match) => {
        if (match.round_number !== round) return false;
        if (round === rounds) return true;
        return match.match_type === 'normal' || !match.match_type;
      });

      const roundX = START_X + (round - 1) * ROUND_SPACING;
      roundMatches.forEach((match, index) => {
        bracketMatches.push({
          match,
          position: {
            x: roundX,
            y: START_Y + index * MATCH_SPACING,
          },
          isThirdPlace: match.match_type === 'third_place',
        });
      });
    }

    return bracketMatches;
  };

  const bracketStructure = calculateBracketStructure();

  // Handle zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.max(0.3, Math.min(3, prev * delta)));
  };

  // Handle pan with improved performance - unified for mouse and touch
  const handleStart = (clientX: number, clientY: number) => {
    setIsDragging(true);
    setDragStart({ x: clientX - position.x, y: clientY - position.y });
  };

  const handleMove = (clientX: number, clientY: number) => {
    if (isDragging) {
      let newX = clientX - dragStart.x;
      let newY = clientY - dragStart.y;

      // Adjust bounds for mobile vs desktop
      const bounds = isMobile
        ? { maxX: 100, minX: -800, maxY: 50, minY: -300 }
        : { maxX: 200, minX: -1000, maxY: 100, minY: -400 };

      newX = Math.max(bounds.minX, Math.min(bounds.maxX, newX));
      newY = Math.max(bounds.minY, Math.min(bounds.maxY, newY));

      // Use requestAnimationFrame for smoother updates
      requestAnimationFrame(() => {
        setPosition({ x: newX, y: newY });
      });
    }
  };

  const handleEnd = () => {
    setIsDragging(false);
  };

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleStart(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    }
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  const handleMouseLeave = () => {
    handleEnd();
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleStart(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging && e.touches.length === 1) {
      e.preventDefault();
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    handleEnd();
  };

  // Reset view - adjust for mobile
  const resetView = () => {
    if (isMobile) {
      setScale(0.5);
      setPosition({ x: 50, y: -100 });
    } else {
      setScale(0.8);
      setPosition({ x: 0, y: -200 });
    }
  };

  const renderMatch = (bracketMatch: BracketMatch) => {
    const { match, position: pos, isThirdPlace } = bracketMatch;
    
    if (!match) return null;

    const team1Names = match.team1Participants.map(p => p.name).join(', ');
    const team2Names = match.team2Participants.map(p => p.name).join(', ');

    return (
      <div
        key={match.id}
        className={`${styles.match} ${isThirdPlace ? styles.thirdPlaceMatch : ''}`}
        style={{
          left: pos.x,
          top: pos.y,
          position: 'absolute'
        }}
      >
        {match.match_type === 'final' && (
          <div className={styles.finalLabel}>Final</div>
        )}
        {isThirdPlace && (
          <div className={styles.thirdPlaceLabel}>3rd Place</div>
        )}
        
        <div className={`${styles.team} ${match.winner_team === 1 ? styles.winnerTeam : ''}`}>
          {team1Names || 'TBD'}
        </div>
        
        <div className={styles.vs}>VS</div>
        
        <div className={`${styles.team} ${match.winner_team === 2 ? styles.winnerTeam : ''}`}>
          {team2Names || 'TBD'}
        </div>

        {match.winner_team && (
          <div className={styles.winner}>
            Winner: {match.winner_team === 1 ? team1Names : team2Names}
          </div>
        )}

        {match.battle_report && (
          <Link
            to={`/battle-report/${match.battle_report}`}
            className={styles.viewBattleReportLink}
          >
            View Battle Report
          </Link>
        )}
      </div>
    );
  };

  return (
    <div className={styles.bracketContainer}>
      <div className={styles.controls}>
        <button onClick={() => setScale(prev => Math.min(3, prev * 1.2))}>Zoom In</button>
        <button onClick={() => setScale(prev => Math.max(0.3, prev * 0.8))}>Zoom Out</button>
        <button onClick={resetView}>Reset View</button>
        <span className={styles.scaleIndicator}>Scale: {Math.round(scale * 100)}%</span>
        <span className={styles.hint}>Drag to pan around the bracket</span>
      </div>
      
      <div
        ref={containerRef}
        className={styles.bracketViewport}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          touchAction: 'none' // Prevent default touch behaviors
        }}
      >
        <div
          className={styles.bracketContent}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: '0 0'
          }}
        >
          {/* Connection lines removed for cleaner bracket view */}

          {bracketStructure.map((bracketMatch, index) => renderMatch(bracketMatch))}
        </div>
      </div>
    </div>
  );
};

export default TournamentBracketTree;
